/*
 * Broadcast Theme — Smooth Slideshow Enhancement
 * Keeps all custom changes outside the core theme.js file.
 */

(function () {
  'use strict';

  /* ============================================
     SETTINGS
  ============================================ */

  const SLIDE_ATTRACTION = 0.018;
  const SLIDE_FRICTION = 0.24;

  /* ============================================
     SMOOTH SLIDESHOW CSS
  ============================================ */

  function addSmoothSlideshowStyles() {
    if (document.getElementById('broadcast-smooth-slideshow-styles')) return;

    const style = document.createElement('style');
    style.id = 'broadcast-smooth-slideshow-styles';

    style.textContent = `

      /* ==========================================
         GENERAL SLIDESHOW PERFORMANCE
      ========================================== */

      .slideshow__slider,
      .slideshow__slider .flickity-viewport,
      .slideshow__slider .flickity-slider,
      .slideshow__slider .slideshow__slide,
      .slideshow__slider .hero__image,
      .slideshow__slider .image__fill,
      .slideshow__slider .image__fill img {
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }

      .slideshow__slider .flickity-slider {
        will-change: transform;
      }

      .slideshow__slider .hero__image,
      .slideshow__slider .image__fill img {
        transform: translateZ(0);
      }


      /* ==========================================
         FADE
      ========================================== */

      .slideshow__slider--fade.is-fade .flickity-cell {
        opacity: 0 !important;
        visibility: hidden;
        transition:
          opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
          visibility 0s linear 0.9s !important;

        will-change: opacity;
        pointer-events: none;
      }

      .slideshow__slider--fade.is-fade .flickity-cell.is-selected {
        opacity: 1 !important;
        visibility: visible;
        transition:
          opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
          visibility 0s linear 0s !important;

        pointer-events: auto;
      }


      /* ==========================================
         ZOOM OUT
      ========================================== */

      .slideshow__slider--zoom-out .image__fill img {
        transform: translateZ(0) scale(1.12);
        transition:
          transform 1.8s cubic-bezier(0.22, 1, 0.36, 1) !important;

        will-change: transform;
      }

      .slideshow__slider--zoom-out .is-selected .image__fill img {
        transform: translateZ(0) scale(1);
      }

      .slideshow__slider--zoom-out.is-fade .flickity-cell {
        opacity: 0 !important;
        visibility: hidden;

        transition:
          opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
          visibility 0s linear 0.9s !important;

        pointer-events: none;
      }

      .slideshow__slider--zoom-out.is-fade .flickity-cell.is-selected {
        opacity: 1 !important;
        visibility: visible;

        transition:
          opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
          visibility 0s linear 0s !important;

        pointer-events: auto;
      }


      /* ==========================================
         WIPE
      ========================================== */

      .slideshow__slider--wipe .slideshow__slide {
        opacity: 1 !important;
      }

      .slideshow__slider--wipe .is-selected .hero__image {
        animation:
          broadcastSmoothWipe
          0.9s
          cubic-bezier(0.22, 1, 0.36, 1)
          both !important;

        will-change: clip-path, transform;
      }

      @keyframes broadcastSmoothWipe {
        0% {
          -webkit-clip-path:
            polygon(100% 0, 100% 0, 100% 100%, 100% 100%);

          clip-path:
            polygon(100% 0, 100% 0, 100% 100%, 100% 100%);

          transform: translate3d(0.8%, 0, 0);
        }

        100% {
          -webkit-clip-path:
            polygon(0 0, 100% 0, 100% 100%, 0 100%);

          clip-path:
            polygon(0 0, 100% 0, 100% 100%, 0 100%);

          transform: translate3d(0, 0, 0);
        }
      }


      /* ==========================================
         HERO TEXT ANIMATION
      ========================================== */

      .aos-initialized
      .slideshow__slider
      [data-aos="hero"].aos-animate {
        animation-duration: 0.85s !important;
        animation-timing-function:
          cubic-bezier(0.22, 1, 0.36, 1) !important;
      }


      /* ==========================================
         ARROWS
      ========================================== */

      .slideshow__slider .flickity-prev-next-button {
        transition:
          opacity 0.3s ease,
          transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
          visibility 0.3s ease !important;
      }


      /* ==========================================
         DOTS
      ========================================== */

      .slideshow__slider .flickity-page-dot {
        transition:
          opacity 0.35s ease,
          background-color 0.35s ease,
          transform 0.35s cubic-bezier(0.22, 1, 0.36, 1) !important;
      }

      .slideshow__slider .flickity-page-dot.is-selected {
        transform: scale(1.08);
      }


      /* ==========================================
         REDUCED MOTION ACCESSIBILITY
      ========================================== */

      @media (prefers-reduced-motion: reduce) {

        .slideshow__slider *,
        .slideshow__slider *::before,
        .slideshow__slider *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }

      }

    `;

    document.head.appendChild(style);
  }


  /* ============================================
     FLICKITY SLIDE PHYSICS
  ============================================ */

  function improveSliderPhysics(slider) {
    if (!slider) return;

    if (!slider.classList.contains('slideshow__slider')) return;

    const flickity = slider.flkty;

    if (!flickity || !flickity.options) return;

    /*
     * Only normal "Slide" transition needs Flickity
     * movement physics.
     *
     * Fade / Zoom / Wipe use their own visual effects.
     */
    if (slider.classList.contains('slideshow__slider--slide')) {
      flickity.options.selectedAttraction = SLIDE_ATTRACTION;
      flickity.options.friction = SLIDE_FRICTION;
    }

    /*
     * Helps avoid tiny visual jumps when the browser
     * recalculates slider measurements.
     */
    requestAnimationFrame(function () {
      if (!slider.flkty) return;

      slider.flkty.resize();
      slider.flkty.reposition();
    });
  }


  /* ============================================
     FIND ALL SLIDESHOWS
  ============================================ */

  function initialiseSmoothSlideshows(root) {
    root = root || document;

    const sliders = root.querySelectorAll(
      '.slideshow__slider'
    );

    sliders.forEach(function (slider) {
      improveSliderPhysics(slider);
    });
  }


  /* ============================================
     INITIAL LOAD
  ============================================ */

  addSmoothSlideshowStyles();

  document.addEventListener('DOMContentLoaded', function () {
    initialiseSmoothSlideshows(document);
  });


  /* ============================================
     WHEN BROADCAST FINISHES INITIALISING SLIDER
  ============================================ */

  document.addEventListener('theme:slider:loaded', function (event) {
    const slider =
      event.detail && event.detail.slider
        ? event.detail.slider
        : null;

    if (!slider) return;

    requestAnimationFrame(function () {
      improveSliderPhysics(slider);
    });
  });


  /* ============================================
     SHOPIFY THEME EDITOR SUPPORT
  ============================================ */

  document.addEventListener('shopify:section:load', function (event) {
    setTimeout(function () {
      initialiseSmoothSlideshows(event.target);
    }, 50);
  });


  /* ============================================
     RESIZE SAFETY
  ============================================ */

  let resizeTimer;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(function () {
      initialiseSmoothSlideshows(document);
    }, 150);
  });

})();